// Best-effort HTTP status extraction across the error shapes this app throws:
// axios errors (.response.status / .response.data.statusCode), APIError-like
// (.status / .statusCode), and plain Errors that name the status in their
// message (e.g. "status: 403", "HTTP 403", "Failed to ...: 403").
// Returns null when no status is confidently identifiable.
export function getHttpStatus(error: unknown, message?: string): number | null {
	const e = error as any;

	const candidates = [
		e?.status,
		e?.statusCode,
		e?.response?.status,
		e?.response?.statusCode,
		e?.response?.data?.statusCode,
		e?.cause?.status,
		e?.cause?.response?.status
	];
	for (const c of candidates) {
		const n = typeof c === 'string' ? Number(c) : c;
		if (typeof n === 'number' && n >= 100 && n < 600) return n;
	}

	const text = `${message ?? ''} ${
		typeof e?.message === 'string' ? e.message : ''
	}`;
	// (1) explicit HTTP-status cue: "status: 403", "status code 404", "HTTP 403".
	let m = text.match(
		/(?:\bstatus(?:\s*code)?\b|\bHTTP(?:\s*error)?\b!?)[:\s]*(\d{3})\b/i
	);
	// (2) trailing ": 4xx" only when the message reads like a failure, e.g.
	//     "Failed to fetch ...: 403" — gated to avoid false matches.
	if (
		!m &&
		/\b(?:fail(?:ed)?|error|unable|could\s*n.?t|denied|forbidden|not\s*found)\b/i.test(
			text
		)
	) {
		m = text.match(/[:\s](4\d\d)\b/);
	}
	if (m) {
		const n = Number(m[1]);
		if (n >= 100 && n < 600) return n;
	}

	return null;
}

// A backend 4xx (401/403/404/409/422/429/...) is an expected, handled
// condition — not a bug to alert on. 5xx and unknown stay visible.
export function isClientError(status: number | null): boolean {
	return status !== null && status >= 400 && status < 500;
}
